import { useEffect, useState } from "react";
import { authService } from "../../services/api";
import { useAuth } from "../../context/AuthProvider.jsx";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { CURRENCY_META, ScopePicker, AcceptedCurrencies, SELECTED_CLS, UNSELECTED_CLS } from "./shared";

/**
 * Painel da Cooud (cartão internacional, Cooud Connect). Sem OAuth: o vendedor
 * CONECTA a conta Cooud que já tem, por convite no e-mail (criar conta não é
 * oferecido aqui). Depois de conectado: verificação (link hospedado da Cooud),
 * moedas e escopo.
 */

const errMsg = (err, fallback) => err?.response?.data?.error || fallback;

const Field = ({ label, children }) => (
    <label className="flex flex-col gap-1 min-w-0">
        <span className="text-[11px] text-white/40">{label}</span>
        {children}
    </label>
);

// Moeda da loja: cards com bandeira (seleção única), no mesmo estilo do
// seletor de "Moedas aceitas".
function StoreCurrencySelect({ value, onChange, options }) {
    return (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" role="radiogroup">
            {(options || ["BRL"]).map((c) => {
                const meta = CURRENCY_META[c] || { flag: null, label: c, symbol: "" };
                const on = value === c;
                return (
                    <button key={c} type="button" role="radio" aria-checked={on} onClick={() => onChange(c)}
                        className={`relative flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all cursor-pointer ${on
                            ? SELECTED_CLS
                            : UNSELECTED_CLS}`}>
                        {meta.flag && (
                            <img alt="" className="w-7 h-5 object-cover rounded-sm" src={`https://flagcdn.com/w40/${meta.flag}.png`} />
                        )}
                        <span className={`text-[11px] font-bold ${on ? "text-white" : "text-white/60"}`}>{meta.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

// ── Painel ─────────────────────────────────────────────────────────────────
export default function CooudConfigPanel({ item, bots, onDone }) {
    const { showError, showSuccess, showInfo } = useNotification();
    const { user } = useAuth();
    const [config, setConfig] = useState(null);
    const [creds, setCreds] = useState(item.credentials || {});
    const [email, setEmail] = useState(user?.email || "");
    const [storeCurrency, setStoreCurrency] = useState((item.credentials || {}).store_currency || "BRL");
    const [currencies, setCurrencies] = useState(
        ["BRL", ...((item.credentials || {}).currencies?.filter((c) => c !== "BRL") || [])]
    );
    const [scope, setScope] = useState(item.upg?.scope === "specific" ? "specific" : "global");
    const [botIds, setBotIds] = useState(item.upg?.scoped_bot_ids || []);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        authService.gateways.cooudConfig()
            .then((res) => {
                setConfig(res.data);
                if (res.data?.credentials) {
                    setCreds(res.data.credentials);
                    if (res.data.credentials.store_currency) setStoreCurrency(res.data.credentials.store_currency);
                }
            })
            .catch(() => setConfig({ configured: false }));
    }, []);

    const connected = Boolean(creds.cooud_account_id);
    const invitePending = !connected && Boolean(creds.invite_sent);
    const chargesBlocked = connected && creds.charges_enabled === false;
    const needsVerification = connected && (
        chargesBlocked || (creds.requirements_due || []).length > 0 || creds.payouts_enabled === false
    );

    const run = async (fn, okMsg, failMsg) => {
        try {
            setBusy(true);
            const res = await fn();
            if (res?.data?.credentials) setCreds(res.data.credentials);
            if (res?.data?.warning) showInfo(res.data.warning);
            else if (okMsg) showSuccess(okMsg);
            return res;
        } catch (err) {
            showError(errMsg(err, failMsg));
            return null;
        } finally {
            setBusy(false);
        }
    };

    // Conta que já estava na Spectrum e não é provadamente sua: confirma por um
    // código enviado ao e-mail da conta Cooud.
    const [proof, setProof] = useState(null); // { email, sentTo }
    const [proofCode, setProofCode] = useState("");

    const sendProofCode = async (targetEmail) => {
        try {
            setBusy(true);
            const res = await authService.gateways.cooudOwnershipStart(targetEmail);
            setProof({ email: targetEmail, sentTo: res.data?.sent_to || targetEmail });
            setProofCode("");
            showInfo(`Enviamos um código para ${res.data?.sent_to || targetEmail}.`);
        } catch (err) {
            showError(errMsg(err, "Erro ao enviar o código."));
        } finally {
            setBusy(false);
        }
    };

    const confirmProof = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            const res = await authService.gateways.cooudOwnershipConfirm(proofCode);
            if (res.data?.credentials) setCreds(res.data.credentials);
            showSuccess("Conta Cooud conectada!");
            onDone?.();
        } catch (err) {
            showError(errMsg(err, "Código inválido."));
        } finally {
            setBusy(false);
        }
    };

    const invite = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            const res = await authService.gateways.cooudInvite(email, storeCurrency);
            if (res.data?.credentials) setCreds(res.data.credentials);
            if (res.data?.status === "connected") { showSuccess("Conta Cooud conectada!"); onDone?.(); }
            else showInfo("Convite enviado! Aceite-o na sua conta Cooud e depois clique em \"Já aceitei\".");
        } catch (err) {
            if (err?.response?.data?.code === "cooud_account_not_owned") {
                setBusy(false);
                await sendProofCode(email.trim().toLowerCase());
                return;
            }
            showError(errMsg(err, "Erro ao enviar convite."));
        } finally {
            setBusy(false);
        }
    };

    const sync = async () => {
        const res = await run(() => authService.gateways.cooudSync(), null, "Erro ao consultar a Cooud.");
        if (!res) return;
        if (res.data?.status === "connected") {
            showSuccess("Status atualizado.");
            if (!connected) onDone?.();
        } else {
            showInfo(res.data?.message || "Convite ainda não aceito.");
        }
    };

    const verify = async () => {
        const res = await run(() => authService.gateways.cooudOnboardingLink(), null, "Erro ao abrir a verificação.");
        if (res?.data?.url) window.location.href = res.data.url;
    };

    const disconnect = async () => {
        if (!window.confirm("Desconectar sua conta Cooud? Os pagamentos por cartão via Cooud param até reconectar.")) return;
        const res = await run(() => authService.gateways.cooudDisconnect(), "Conta Cooud desconectada.", "Erro ao desconectar.");
        if (res) onDone?.();
    };

    const save = async () => {
        if (scope === "specific" && botIds.length === 0) { showError("Selecione ao menos um bot."); return; }
        const res = await run(async () => {
            const r = await authService.gateways.cooudSaveSettings(currencies, storeCurrency);
            await authService.gateways.saveScope(item.gateway.name, scope, scope === "specific" ? botIds : []);
            return r;
        }, "Configuração salva.", "Erro ao salvar.");
        if (res && !res.data?.warning) onDone?.();
    };

    const toggleCurrency = (c) => {
        if (c === "BRL") return;
        setCurrencies((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
    };
    const toggleBot = (id) => setBotIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    if (!config) return <p className="text-sm text-white/40">Carregando...</p>;

    if (!config.configured) {
        return (
            <div className="spt-form">
                <p className="text-gray-400" style={{ fontSize: 13 }}>A Cooud ainda não está configurada no servidor.</p>
                <div className="spt-form-actions">
                    <button type="button" className="btn-secondary" onClick={() => onDone?.()}>Fechar</button>
                </div>
            </div>
        );
    }

    // ── Não conectado ──
    if (!connected) {
        if (invitePending) {
            return (
                <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                        <p className="text-sm font-medium text-amber-400">Convite pendente</p>
                        <p className="text-xs text-white/50">
                            Enviamos um convite para <strong className="text-white/70">{creds.email}</strong>. Entre na
                            sua conta Cooud com esse e-mail, revise a taxa e aceite. O convite vale 7 dias. A conta
                            precisa ter uma loja criada na Cooud para ser conectada.
                        </p>
                        {creds.invite_url && (
                            <a href={creds.invite_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-400)] underline">Abrir convite</a>
                        )}
                    </div>
                    <div className="spt-form-actions">
                        <button type="button" className="btn-secondary" disabled={busy}
                            onClick={() => setCreds((c) => ({ ...c, invite_sent: false }))}>
                            Usar outro e-mail
                        </button>
                        <button type="button" className="btn-primary" onClick={sync} disabled={busy}>
                            {busy ? "Verificando..." : "Já aceitei"}
                        </button>
                    </div>
                </div>
            );
        }

        if (proof) {
            return (
                <form onSubmit={confirmProof} className="space-y-3">
                    <p className="text-xs text-white/50">
                        Essa conta Cooud já está na Spectrum. Para confirmar que ela é sua, enviamos um código de
                        6 dígitos para <strong className="text-white/70">{proof.sentTo}</strong>. Ele vale 15 minutos.
                    </p>
                    <Field label="Código">
                        <input className="input2 w-full tracking-[0.4em] text-center text-lg" inputMode="numeric"
                            autoComplete="one-time-code" maxLength={6} placeholder="000000" value={proofCode}
                            onChange={(e) => setProofCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required />
                    </Field>
                    <div className="spt-form-actions">
                        <button type="button" className="btn-secondary" disabled={busy}
                            onClick={() => sendProofCode(proof.email)}>Reenviar código</button>
                        <button type="submit" className="btn-primary" disabled={busy || proofCode.length !== 6}>
                            {busy ? "Confirmando..." : "Confirmar"}
                        </button>
                    </div>
                    <button type="button" className="text-[11px] text-white/40 hover:text-white/70"
                        onClick={() => { setProof(null); setProofCode(""); }}>Usar outro e-mail</button>
                </form>
            );
        }

        // Aqui só se CONECTA uma conta Cooud existente (convite no e-mail).
        return (
            <form onSubmit={invite} className="space-y-3">
                <p className="text-gray-400" style={{ fontSize: 13 }}>
                    Conecte sua conta Cooud para receber por cartão internacional. A cobrança é feita na sua loja
                    Cooud; a Spectrum recebe a comissão automaticamente.
                    {config.sandbox && <span className="text-amber-400/80"> (ambiente de teste)</span>}
                </p>
                <p className="text-xs text-white/50">
                    Informe o e-mail da sua conta Cooud. Você recebe um convite para conectá-la à Spectrum e
                    vê a taxa antes de confirmar.
                </p>
                <Field label="E-mail da conta Cooud">
                    <input type="email" className="input2 w-full" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </Field>
                <Field label="Moeda da sua loja Cooud">
                    <StoreCurrencySelect value={storeCurrency} onChange={setStoreCurrency} options={config.store_currencies} />
                </Field>
                <div className="spt-form-actions">
                    <button type="button" className="btn-secondary" onClick={() => onDone?.()}>Cancelar</button>
                    <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Enviando..." : "Enviar convite"}</button>
                </div>
            </form>
        );
    }

    // ── Conectado ──
    return (
        <div className="min-w-0 w-full flex flex-col gap-4 overflow-y-auto">
            <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-check-circle text-white/50" />
                        {chargesBlocked
                            ? <p className="text-sm font-medium text-red-400">Cooud conectada — cobranças bloqueadas</p>
                            : needsVerification
                                ? <p className="text-sm font-medium text-amber-400">Cooud conectada — verificação pendente</p>
                                : <p className="text-sm font-medium text-emerald-400">Cooud Conectada</p>}
                    </div>
                    <p className="text-xs text-white/50">
                        Conta:{" "}
                        <code className="px-1 py-0.5 bg-white/[0.02] rounded text-xs text-white/60">{creds.cooud_account_id}</code>
                        {creds.livemode === false && <span className="text-white/40"> (modo teste)</span>}
                    </p>
                    {creds.email && <p className="text-xs text-white/50">E-mail: {creds.email}</p>}
                    <p className="text-xs text-white/50">
                        Cobranças: {creds.charges_enabled === false ? <span className="text-red-400">bloqueadas</span> : <span className="text-emerald-400">liberadas</span>}
                        {" · "}Repasses: {creds.payouts_enabled ? <span className="text-emerald-400">liberados</span> : <span className="text-amber-400">pendentes</span>}
                    </p>
                </div>

                {needsVerification && (
                    <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/15 space-y-2">
                        <p className="text-xs text-amber-400/90">
                            {chargesBlocked
                                ? "A Cooud bloqueou as cobranças desta conta até a verificação ser concluída. Enquanto isso, a Spectrum não usa a Cooud para cobrar (usa o próximo cartão da fila, se houver)."
                                : "A Cooud ainda pede informações para liberar os repasses. As vendas funcionam, mas o dinheiro fica retido até concluir."}
                            {(creds.requirements_due || []).length > 0 && ` (${creds.requirements_due.length} pendência(s))`}
                        </p>
                        <button type="button" className="btn-primary" onClick={verify} disabled={busy}>Completar verificação</button>
                    </div>
                )}

                <div className="space-y-2">
                    <label className="text-xs font-medium text-white/40">Moeda da loja Cooud</label>
                    <StoreCurrencySelect value={storeCurrency} onChange={setStoreCurrency} options={config.store_currencies} />
                    <p className="text-[11px] text-white/40">Fixa na Cooud a partir da primeira venda. Cobranças em outras moedas são convertidas.</p>
                </div>

                <div className="space-y-2">
                    <label className="text-xs font-medium text-white/40">Moedas aceitas</label>
                    <AcceptedCurrencies value={currencies} onToggle={toggleCurrency} />
                    <p className="text-[11px] text-white/40">Em fluxos globais, o cliente escolhe entre as moedas selecionadas.</p>
                </div>

                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={sync} disabled={busy}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all cursor-pointer disabled:opacity-50 border hover:border-white/10 h-10 px-4 py-2 flex-1 bg-white/[0.04] border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.06]">
                        <i className="fas fa-sync-alt mr-2" />Atualizar status
                    </button>
                    <button type="button" onClick={disconnect} disabled={busy}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all cursor-pointer disabled:opacity-50 shrink-0 border hover:border-white/10 h-10 px-4 py-2 bg-white/[0.04] border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <i className="fas fa-trash mr-2" />Desconectar
                    </button>
                </div>

                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-1">
                    <p className="text-xs text-white/50">
                        <span className="text-[var(--brand-400)] font-medium">Split automático:</span> a comissão da plataforma vai
                        via <code className="px-1 py-0.5 bg-white/[0.02] rounded text-xs">application_fee_amount</code>, dentro do teto que você aceitou.
                    </p>
                    <p className="text-xs text-white/50">
                        <span className="text-[var(--brand-400)] font-medium">Cartões internacionais</span> e carteiras (Apple Pay / Google Pay, quando elegíveis).
                    </p>
                </div>

                <ScopePicker scope={scope} setScope={setScope} bots={bots} botIds={botIds} toggleBot={toggleBot} />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 pt-3 shrink-0 border-t border-white/[0.06]">
                <button type="button" onClick={() => onDone?.()} className="btn-secondary">Cancelar</button>
                <button type="button" onClick={save} disabled={busy} className="btn-primary">{busy ? "Salvando..." : "Salvar"}</button>
            </div>
        </div>
    );
}
